import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router';
import {
    ArrowLeft,
    UserPlus01,
    Mail01,
    CheckCircle,
    XCircle,
    Users01,
    X,
    Edit01,
    Trash01,
    Send01,
    AlertTriangle,
} from '@untitledui/icons';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { ModalOverlay, Modal, Dialog } from '@/components/application/modals/modal';
import { LoadingIndicator } from '@/components/application/loading-indicator/loading-indicator';
import { Dropdown } from '@/components/base/dropdown/dropdown';
import { Checkbox } from '@/components/base/checkbox/checkbox';
import { getCompany, getCompanyInvitations, inviteTeamMember, createTeamMemberDirect, updateTeamMember, removeTeamMember, resendInvitation } from '@/data/api/companies';
import { getUsers } from '@/data/api/users';
import type { Company } from '@/types/company';
import type { User } from '@/data/api/users';
import { useTranslation } from 'react-i18next';
import { toast } from '@/lib/toast-service';
import { ResetUserDataModal } from './reset-user-data-modal';

type ActivationMethod = 'email_invitation' | 'direct_activation';

export const CompanyTeamPage = () => {
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation(['pages:companies', 'common']);
    const [company, setCompany] = useState<Company | null>(null);
    const [users, setUsers] = useState<User[]>([]);
    const [invitations, setInvitations] = useState<Array<{
        invitation_id: string;
        email: string;
        role: string;
        invited_by_user_id: string;
        expires_at: string;
        created_at: string;
    }>>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newMemberEmail, setNewMemberEmail] = useState('');
    const [newMemberFirstName, setNewMemberFirstName] = useState('');
    const [newMemberLastName, setNewMemberLastName] = useState('');
    const [newMemberRole, setNewMemberRole] = useState<'operator' | 'company_admin'>('operator');
    const [newMemberActivationMethod, setNewMemberActivationMethod] = useState<ActivationMethod>('email_invitation');
    const [newMemberPassword, setNewMemberPassword] = useState('');
    const [newMemberPasswordConfirm, setNewMemberPasswordConfirm] = useState('');

    // Edit modal state
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [editRole, setEditRole] = useState<'operator' | 'company_admin'>('operator');
    const [editIsActive, setEditIsActive] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);

    // Remove modal state
    const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
    const [removingUser, setRemovingUser] = useState<User | null>(null);
    const [sendNotificationEmail, setSendNotificationEmail] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);

    // Resend invitation state
    const [resendingInvitationId, setResendingInvitationId] = useState<string | null>(null);

    // Reset data modal state
    const [resetDataUser, setResetDataUser] = useState<User | null>(null);

    const loadTeamData = async (includeCompany = false) => {
        if (!id) return;

        try {
            if (includeCompany) {
                setIsLoading(true);
                const [companyData, usersData, invitationsData] = await Promise.all([
                    getCompany(id),
                    getUsers({ company_id: id, limit: 100 }),
                    getCompanyInvitations(id)
                ]);
                setCompany(companyData);
                setUsers(usersData);
                setInvitations(invitationsData);
                setIsLoading(false);
            } else {
                const [usersData, invitationsData] = await Promise.all([
                    getUsers({ company_id: id, limit: 100 }),
                    getCompanyInvitations(id)
                ]);
                setUsers(usersData);
                setInvitations(invitationsData);
            }
        } catch (error) {
            console.error('Error loading team data:', error);
            toast.error(t('team.errorLoading'));
            if (includeCompany) {
                setIsLoading(false);
            }
        }
    };

    useEffect(() => {
        loadTeamData(true);
    }, [id, t]);

    const getRoleBadge = (role: string) => {
        const roleMap: Record<string, { label: string; color: 'blue' | 'success' | 'warning' | 'gray' }> = {
            'SUPER_ADMIN': { label: t('team.roles.superAdmin'), color: 'blue' },
            'COMPANY_ADMIN': { label: t('team.roles.companyAdmin'), color: 'success' },
            'OPERATOR': { label: t('team.roles.operator'), color: 'gray' },
            'VIEWER': { label: t('team.roles.viewer'), color: 'gray' },
        };

        const roleInfo = roleMap[role] || { label: role, color: 'gray' };
        return <Badge color={roleInfo.color} size="sm">{roleInfo.label}</Badge>;
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('it-IT', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const isInvitationExpired = (expiresAt: string) => {
        return new Date(expiresAt) < new Date();
    };

    const roleOptions = [
        { id: 'operator', label: t('team.roles.operator') },
        { id: 'company_admin', label: t('team.roles.companyAdmin') },
    ];
    const activationMethodOptions = [
        { id: 'email_invitation', label: t('team.form.activationMethods.emailInvitation') },
        { id: 'direct_activation', label: t('team.form.activationMethods.directActivation') },
    ];
    const closeAddMemberModal = () => {
        setIsAddMemberModalOpen(false);
        setNewMemberEmail('');
        setNewMemberFirstName('');
        setNewMemberLastName('');
        setNewMemberRole('operator');
        setNewMemberActivationMethod('email_invitation');
        setNewMemberPassword('');
        setNewMemberPasswordConfirm('');
    };

    const handleAddMember = async () => {
        if (!id || !newMemberEmail.trim()) {
            toast.error(t('team.error.emailRequired'));
            return;
        }
        if (newMemberActivationMethod === 'direct_activation') {
            if (!newMemberPassword || !newMemberPasswordConfirm) {
                toast.error(t('team.error.passwordRequired'));
                return;
            }
            if (newMemberPassword.length < 8) {
                toast.error(t('team.error.passwordMinLength'));
                return;
            }
            if (newMemberPassword !== newMemberPasswordConfirm) {
                toast.error(t('team.error.passwordMismatch'));
                return;
            }
        }

        setIsSubmitting(true);
        try {
            if (newMemberActivationMethod === 'email_invitation') {
                await inviteTeamMember(id, {
                    email: newMemberEmail.trim(),
                    first_name: newMemberFirstName.trim() || undefined,
                    last_name: newMemberLastName.trim() || undefined,
                    role: newMemberRole,
                });
                toast.success(t('team.success.memberInvitationSent'));
            } else {
                await createTeamMemberDirect(id, {
                    email: newMemberEmail.trim(),
                    first_name: newMemberFirstName.trim() || undefined,
                    last_name: newMemberLastName.trim() || undefined,
                    role: newMemberRole,
                    password: newMemberPassword,
                    password_confirm: newMemberPasswordConfirm,
                });
                toast.success(t('team.success.memberCreatedDirect'));
            }

            closeAddMemberModal();

            // Reload data to show the new invitation in pending invitations
            await loadTeamData();
        } catch (error: any) {
            console.error('Error adding team member:', error);
            toast.error(error.message || t('team.error.addingMember'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEditClick = (user: User) => {
        setEditingUser(user);
        setEditRole(user.user_role === 'COMPANY_ADMIN' ? 'company_admin' : 'operator');
        setEditIsActive(user.is_active);
        setIsEditModalOpen(true);
    };

    const handleUpdateMember = async () => {
        if (!id || !editingUser) return;

        setIsUpdating(true);
        try {
            await updateTeamMember(id, editingUser.user_id, {
                role: editRole,
                is_active: editIsActive,
            });

            toast.success(t('team.success.memberUpdated'));
            setIsEditModalOpen(false);
            setEditingUser(null);
            await loadTeamData();
        } catch (error: any) {
            console.error('Error updating team member:', error);
            toast.error(error.message || t('team.error.updatingMember'));
        } finally {
            setIsUpdating(false);
        }
    };

    const handleRemoveClick = (user: User) => {
        setRemovingUser(user);
        setSendNotificationEmail(false);
        setIsRemoveModalOpen(true);
    };

    const handleRemoveMember = async () => {
        if (!id || !removingUser) return;

        setIsRemoving(true);
        try {
            await removeTeamMember(id, removingUser.user_id, {
                send_notification_email: sendNotificationEmail,
            });

            toast.success(t('team.success.memberRemoved'));
            setIsRemoveModalOpen(false);
            setRemovingUser(null);
            setSendNotificationEmail(false);
            await loadTeamData();
        } catch (error: any) {
            console.error('Error removing team member:', error);
            toast.error(error.message || t('team.error.removingMember'));
        } finally {
            setIsRemoving(false);
        }
    };

    const handleResendInvitation = async (invitationId: string) => {
        if (!id) return;

        setResendingInvitationId(invitationId);
        try {
            await resendInvitation(id, invitationId);
            toast.success(t('team.success.invitationResent'));
        } catch (error: any) {
            console.error('Error resending invitation:', error);
            toast.error(error.message || t('team.error.resendingInvitation'));
        } finally {
            setResendingInvitationId(null);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('team.loading')}</p>
                </div>
            </div>
        );
    }

    if (!company) {
        return (
            <div className="text-center py-12">
                <XCircle className="size-12 mx-auto mb-4 text-error-solid" />
                <h3 className="text-lg font-medium text-primary mb-2">{t('team.companyNotFound')}</h3>
                <Link
                    to="/admin/companies"
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                >
                    <ArrowLeft className="size-5" />
                    {t('team.backToCompanies')}
                </Link>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Link
                        to="/admin/companies"
                        className="inline-flex items-center justify-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                    >
                        <ArrowLeft className="size-4" />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-semibold text-primary">{t('team.title', { companyName: company.company_name })}</h1>
                        <p className="text-tertiary mt-1">
                            {t('team.subtitle')}
                        </p>
                    </div>
                </div>
                <Button
                    onClick={() => setIsAddMemberModalOpen(true)}
                    size="md"
                    color="primary"
                    iconLeading={UserPlus01}
                >
                    {t('team.addMember')}
                </Button>
            </div>

            {/* Active Users */}
            <TableCard.Root>
                <TableCard.Header
                    title={`${t('team.activeMembers')} (${users.length})`}
                    description={t('team.activeMembersDescription')}
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('team.table.headers.name')} isRowHeader />
                        <Table.Head label={t('team.table.headers.email')} />
                        <Table.Head label={t('team.table.headers.role')} />
                        <Table.Head label={t('team.table.headers.status')} />
                        <Table.Head label={t('team.table.headers.joinedAt')} />
                        <Table.Head label="" />
                    </Table.Header>
                    <Table.Body>
                        {users.map((user) => (
                            <Table.Row key={user.user_id}>
                                <Table.Cell>
                                    <div className="font-medium text-primary">
                                        {user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email}
                                    </div>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">{user.email}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    {getRoleBadge(user.user_role)}
                                </Table.Cell>
                                <Table.Cell>
                                    {user.is_active ? (
                                        <Badge color="success" size="sm">
                                            <CheckCircle className="size-3 mr-1" />
                                            {t('team.status.active')}
                                        </Badge>
                                    ) : (
                                        <Badge color="error" size="sm">
                                            <XCircle className="size-3 mr-1" />
                                            {t('team.status.inactive')}
                                        </Badge>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">
                                        {formatDate(user.created_at)}
                                    </span>
                                </Table.Cell>
                                <Table.Cell>
                                    <Dropdown.Root>
                                        <Dropdown.DotsButton />
                                        <Dropdown.Popover className="w-min">
                                            <Dropdown.Menu>
                                                <Dropdown.Item
                                                    icon={Edit01}
                                                    onAction={() => handleEditClick(user)}
                                                >
                                                    {t('team.actions.edit')}
                                                </Dropdown.Item>
                                                <Dropdown.Item
                                                    icon={Trash01}
                                                    onAction={() => handleRemoveClick(user)}
                                                    className="text-error-solid"
                                                >
                                                    {t('team.actions.remove')}
                                                </Dropdown.Item>
                                                <Dropdown.Separator />
                                                <Dropdown.Item
                                                    icon={AlertTriangle}
                                                    onAction={() => setResetDataUser(user)}
                                                    className="text-error-solid"
                                                >
                                                    {t('team.resetData.menuItem')}
                                                </Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown.Popover>
                                    </Dropdown.Root>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>

                {users.length === 0 && (
                    <div className="text-center py-12">
                        <Users01 className="size-12 mx-auto mb-4 text-tertiary" />
                        <p className="text-tertiary">{t('team.noActiveMembers')}</p>
                    </div>
                )}
            </TableCard.Root>

            {/* Pending Invitations */}
            {invitations.length > 0 && (
                <TableCard.Root>
                    <TableCard.Header
                        title={`${t('team.pendingInvitations')} (${invitations.length})`}
                        description={t('team.pendingInvitationsDescription')}
                    />

                    <Table>
                        <Table.Header>
                            <Table.Head label={t('team.table.headers.email')} isRowHeader />
                            <Table.Head label={t('team.table.headers.role')} />
                            <Table.Head label={t('team.table.headers.invitedAt')} />
                            <Table.Head label={t('team.table.headers.expiresAt')} />
                            <Table.Head label={t('team.table.headers.status')} />
                            <Table.Head label="" />
                        </Table.Header>
                        <Table.Body>
                            {invitations.map((invitation) => (
                                <Table.Row key={invitation.invitation_id}>
                                    <Table.Cell>
                                        <div className="flex items-center gap-2">
                                            <Mail01 className="size-4 text-tertiary" />
                                            <span className="font-medium text-primary">{invitation.email}</span>
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        {getRoleBadge(invitation.role)}
                                    </Table.Cell>
                                    <Table.Cell>
                                        <span className="text-sm text-tertiary">
                                            {formatDate(invitation.created_at)}
                                        </span>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <span className={`text-sm ${isInvitationExpired(invitation.expires_at) ? 'text-error-solid' : 'text-tertiary'}`}>
                                            {formatDate(invitation.expires_at)}
                                        </span>
                                    </Table.Cell>
                                    <Table.Cell>
                                        {isInvitationExpired(invitation.expires_at) ? (
                                            <Badge color="error" size="sm">
                                                {t('team.status.expired')}
                                            </Badge>
                                        ) : (
                                            <Badge color="warning" size="sm">
                                                {t('team.status.pending')}
                                            </Badge>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {!isInvitationExpired(invitation.expires_at) && (
                                            <Button
                                                onClick={() => handleResendInvitation(invitation.invitation_id)}
                                                size="sm"
                                                color="secondary"
                                                iconLeading={Send01}
                                                isLoading={resendingInvitationId === invitation.invitation_id}
                                                disabled={!!resendingInvitationId}
                                            >
                                                {t('team.actions.resend')}
                                            </Button>
                                        )}
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                </TableCard.Root>
            )}

            {/* Add Member Modal */}
            <ModalOverlay isOpen={isAddMemberModalOpen} onOpenChange={(open) => !open && closeAddMemberModal()}>
                <Modal>
                    <Dialog>
                        <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary w-full max-w-md">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-semibold text-primary">
                                    {t('team.addMember')}
                                </h2>
                                <button
                                    onClick={closeAddMemberModal}
                                    className="text-tertiary hover:text-primary"
                                    aria-label={t('common:buttons.close')}
                                >
                                    <X className="size-5" />
                                </button>
                            </div>

                            <div className="space-y-4">
                                <Input
                                    label={t('team.form.email')}
                                    placeholder={t('team.form.emailPlaceholder')}
                                    value={newMemberEmail}
                                    onChange={setNewMemberEmail}
                                    type="email"
                                    isRequired
                                    size="md"
                                />

                                <div className="grid grid-cols-2 gap-4">
                                    <Input
                                        label={t('team.form.firstName')}
                                        placeholder={t('team.form.firstNamePlaceholder')}
                                        value={newMemberFirstName}
                                        onChange={setNewMemberFirstName}
                                        size="md"
                                    />

                                    <Input
                                        label={t('team.form.lastName')}
                                        placeholder={t('team.form.lastNamePlaceholder')}
                                        value={newMemberLastName}
                                        onChange={setNewMemberLastName}
                                        size="md"
                                    />
                                </div>

                                <Select
                                    label={t('team.form.role')}
                                    items={roleOptions}
                                    selectedKey={newMemberRole}
                                    onSelectionChange={(key) => setNewMemberRole(key as 'operator' | 'company_admin')}
                                    size="md"
                                >
                                    {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                                </Select>

                                <Select
                                    label={t('team.form.activationMethod')}
                                    items={activationMethodOptions}
                                    selectedKey={newMemberActivationMethod}
                                    onSelectionChange={(key) => setNewMemberActivationMethod(key as ActivationMethod)}
                                    size="md"
                                >
                                    {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                                </Select>

                                {newMemberActivationMethod === 'direct_activation' && (
                                    <div className="grid grid-cols-1 gap-4">
                                        <Input
                                            label={t('team.form.password')}
                                            placeholder={t('team.form.passwordPlaceholder')}
                                            value={newMemberPassword}
                                            onChange={setNewMemberPassword}
                                            type="password"
                                            isRequired
                                            size="md"
                                        />
                                        <Input
                                            label={t('team.form.passwordConfirm')}
                                            placeholder={t('team.form.passwordConfirmPlaceholder')}
                                            value={newMemberPasswordConfirm}
                                            onChange={setNewMemberPasswordConfirm}
                                            type="password"
                                            isRequired
                                            size="md"
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-end gap-3 mt-6">
                                <Button
                                    onClick={closeAddMemberModal}
                                    color="secondary"
                                    size="md"
                                >
                                    {t('common:buttons.cancel')}
                                </Button>
                                <Button
                                    onClick={handleAddMember}
                                    color="primary"
                                    size="md"
                                    isLoading={isSubmitting}
                                    disabled={
                                        !newMemberEmail.trim() ||
                                        (newMemberActivationMethod === 'direct_activation' &&
                                            (!newMemberPassword || !newMemberPasswordConfirm))
                                    }
                                >
                                    {t('team.form.addButton')}
                                </Button>
                            </div>
                        </div>
                    </Dialog>
                </Modal>
            </ModalOverlay>

            {/* Loading Modal */}
            <ModalOverlay isOpen={isSubmitting} onOpenChange={() => { }}>
                <Modal>
                    <Dialog>
                        <div className="rounded-xl bg-primary p-8 shadow-lg ring-1 ring-secondary">
                            <div className="flex flex-col items-center gap-4">
                                <LoadingIndicator type="line-spinner" size="lg" />
                                <p className="text-lg font-semibold text-primary">
                                    {t('team.form.addingMember')}
                                </p>
                            </div>
                        </div>
                    </Dialog>
                </Modal>
            </ModalOverlay>

            {/* Edit Member Modal */}
            <ModalOverlay isOpen={isEditModalOpen} onOpenChange={(open) => !open && setIsEditModalOpen(false)}>
                <Modal>
                    <Dialog>
                        <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary w-full max-w-md">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-semibold text-primary">
                                    {t('team.editMember.title')}
                                </h2>
                                <button
                                    onClick={() => setIsEditModalOpen(false)}
                                    className="text-tertiary hover:text-primary"
                                    aria-label={t('common:buttons.close')}
                                >
                                    <X className="size-5" />
                                </button>
                            </div>

                            {editingUser && (
                                <div className="space-y-4">
                                    <div>
                                        <p className="text-sm font-medium text-primary mb-1">
                                            {t('team.editMember.name')}
                                        </p>
                                        <p className="text-sm text-tertiary">
                                            {editingUser.full_name || `${editingUser.first_name || ''} ${editingUser.last_name || ''}`.trim() || editingUser.email}
                                        </p>
                                    </div>

                                    <div>
                                        <p className="text-sm font-medium text-primary mb-1">
                                            {t('team.editMember.email')}
                                        </p>
                                        <p className="text-sm text-tertiary">
                                            {editingUser.email}
                                        </p>
                                    </div>

                                    <Select
                                        label={t('team.form.role')}
                                        items={roleOptions}
                                        selectedKey={editRole}
                                        onSelectionChange={(key) => setEditRole(key as 'operator' | 'company_admin')}
                                        size="md"
                                    >
                                        {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                                    </Select>

                                    <Checkbox
                                        isSelected={editIsActive}
                                        onChange={setEditIsActive}
                                        label={t('team.editMember.isActive')}
                                    />
                                </div>
                            )}

                            <div className="flex justify-end gap-3 mt-6">
                                <Button
                                    onClick={() => setIsEditModalOpen(false)}
                                    color="secondary"
                                    size="md"
                                    disabled={isUpdating}
                                >
                                    {t('common:buttons.cancel')}
                                </Button>
                                <Button
                                    onClick={handleUpdateMember}
                                    color="primary"
                                    size="md"
                                    isLoading={isUpdating}
                                >
                                    {t('team.editMember.saveButton')}
                                </Button>
                            </div>
                        </div>
                    </Dialog>
                </Modal>
            </ModalOverlay>

            {/* Remove Member Confirmation Modal */}
            <ModalOverlay isOpen={isRemoveModalOpen} onOpenChange={(open) => !open && setIsRemoveModalOpen(false)}>
                <Modal>
                    <Dialog>
                        <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary w-full max-w-md">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-semibold text-primary">
                                    {t('team.removeMember.title')}
                                </h2>
                                <button
                                    onClick={() => setIsRemoveModalOpen(false)}
                                    className="text-tertiary hover:text-primary"
                                    aria-label={t('common:buttons.close')}
                                    disabled={isRemoving}
                                >
                                    <X className="size-5" />
                                </button>
                            </div>

                            {removingUser && (
                                <div className="space-y-4">
                                    <p className="text-sm text-tertiary">
                                        {t('team.removeMember.confirmation', {
                                            name: removingUser.full_name || `${removingUser.first_name || ''} ${removingUser.last_name || ''}`.trim() || removingUser.email,
                                            email: removingUser.email
                                        })}
                                    </p>

                                    <div className="p-4 bg-secondary rounded-lg">
                                        <Checkbox
                                            isSelected={sendNotificationEmail}
                                            onChange={setSendNotificationEmail}
                                            label={t('team.removeMember.sendNotificationEmail')}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-end gap-3 mt-6">
                                <Button
                                    onClick={() => setIsRemoveModalOpen(false)}
                                    color="secondary"
                                    size="md"
                                    disabled={isRemoving}
                                >
                                    {t('common:buttons.cancel')}
                                </Button>
                                <Button
                                    onClick={handleRemoveMember}
                                    color="primary-destructive"
                                    size="md"
                                    isLoading={isRemoving}
                                >
                                    {t('team.removeMember.confirmButton')}
                                </Button>
                            </div>
                        </div>
                    </Dialog>
                </Modal>
            </ModalOverlay>

            {/* Reset User Data Modal */}
            <ResetUserDataModal
                user={resetDataUser}
                isOpen={!!resetDataUser}
                onClose={() => setResetDataUser(null)}
            />
        </div>
    );
};

